'use client';

import { c as _c } from "react/compiler-runtime";
import { KitAvatar } from '@repo/features/avatar';
import { z } from '@repo/schema';
import { GetUserSchema } from '@repo/schema/user';
import { formatUserName } from '@repo/sdk/helpers';
import { Avatar, AvatarFallback, AvatarGroup, AvatarGroupCount, Button, Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList, CommandShortcut, Field, FieldContent, FieldError, FieldLabel, Popover, PopoverContent, PopoverTrigger, Skeleton, Spinner, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui/components';
import { Check, CircleUserRound, Minus, Pencil, UsersRound } from '@repo/ui/icons';
import { cn } from '@repo/ui/lib/utils';
import { type FocusEvent, type ReactNode, useEffect, useId, useRef, useState } from 'react';
import { useReadOnly } from '../../components/readonly.tsx';
import { useFieldContext } from '../../lib/form-context.ts';
export type FormUserMultiSelectGroup = {
  id: number | string;
  label: string;
  users: FormUserMultiSelectUser[];
};
export type FormUserMultiSelectProps = UserMultiSelectBaseProps;
export type FormUserMultiSelectUser = z.infer<typeof GetUserSchema>;
export type UserMultiSelectProps = UserMultiSelectBaseProps & {
  onChange: (_users: FormUserMultiSelectUser[]) => void;
  value: FormUserMultiSelectUser[];
};
type UserMultiSelectBaseProps = {
  className?: string;
  compact?: boolean;
  currentUser?: FormUserMultiSelectUser;
  disabled?: boolean;
  groups?: FormUserMultiSelectGroup[];
  id?: string;
  invalid?: boolean;
  isLoading?: boolean;
  isPending?: boolean;
  label?: string;
  onBlur?: () => void;
  onOpenChange?: (_open: boolean) => void;
  onSearchValueChange: (_value: string) => void;
  placeholder?: string;
  plainText?: boolean;
  readOnly?: boolean;
  searchPlaceholder?: string;
  searchValue: string;
  users: FormUserMultiSelectUser[];
};
const MAX_VISIBLE_USERS = 3;
const TRIGGER_CLASS_NAME = 'border-0 shadow-none outline-none focus-visible:scale-110 focus-visible:border-transparent focus-visible:ring-0 aria-invalid:border-transparent aria-invalid:ring-0 dark:aria-invalid:border-transparent dark:aria-invalid:ring-0';
const EDIT_TRIGGER_HOVER_CLASS_NAME = 'pointer-events-none opacity-0 transition-opacity group-hover/assignee-editor:pointer-events-auto group-hover/assignee-editor:opacity-100 group-focus-within/assignee-editor:pointer-events-auto group-focus-within/assignee-editor:opacity-100';
const AVATAR_GROUP_COUNT_BORDER_CLASS_NAME = 'after:absolute after:inset-0 after:rounded-full after:border after:border-border after:mix-blend-darken dark:after:mix-blend-lighten';
const dedupeUsers = (users: FormUserMultiSelectUser[]) => {
  const seen = new Set<number>();
  return users.filter(user => {
    if (seen.has(user.id)) {
      return false;
    }
    seen.add(user.id);
    return true;
  });
};
const haveSameUserIds = (currentUsers: FormUserMultiSelectUser[], nextUsers: FormUserMultiSelectUser[]) => currentUsers.length === nextUsers.length && currentUsers.every((user, index) => user.id === nextUsers[index]?.id);
const UserAvatar = t0 => {
  const $ = _c(5);
  const {
    user
  } = t0;
  let t1;
  if ($[0] !== user) {
    t1 = formatUserName(user);
    $[0] = user;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== t1 || $[3] !== user) {
    t2 = <KitAvatar alt={t1} size="sm" user={user} data-sentry-element="KitAvatar" data-sentry-component="UserAvatar" data-sentry-source-file="form-user-multi-select.tsx" />;
    $[2] = t1;
    $[3] = user;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const UserAvatarWithTooltip = t0 => {
  const $ = _c(10);
  const {
    user
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = <span className="inline-flex" />;
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  let t2;
  if ($[1] !== user) {
    t2 = <TooltipTrigger render={t1} data-sentry-element="TooltipTrigger" data-sentry-source-file="form-user-multi-select.tsx"><UserAvatar user={user} data-sentry-element="UserAvatar" data-sentry-source-file="form-user-multi-select.tsx" /></TooltipTrigger>;
    $[1] = user;
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  let t3;
  if ($[3] !== user) {
    t3 = formatUserName(user);
    $[3] = user;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== t3) {
    t4 = <TooltipContent data-sentry-element="TooltipContent" data-sentry-source-file="form-user-multi-select.tsx">{t3}</TooltipContent>;
    $[5] = t3;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t2 || $[8] !== t4) {
    t5 = <Tooltip data-sentry-element="Tooltip" data-sentry-component="UserAvatarWithTooltip" data-sentry-source-file="form-user-multi-select.tsx">{t2}{t4}</Tooltip>;
    $[7] = t2;
    $[8] = t4;
    $[9] = t5;
  } else {
    t5 = $[9];
  }
  return t5;
};
const SelectedUsers = t0 => {
  const $ = _c(43);
  const {
    compact: t1,
    editTrigger,
    placeholder,
    plainText: t2,
    users
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const plainText = t2 === undefined ? false : t2;
  if (plainText) {
    const t3 = users.length === 0 && "text-muted-foreground";
    let t4;
    if ($[0] !== t3) {
      t4 = cn("min-w-0 truncate", t3);
      $[0] = t3;
      $[1] = t4;
    } else {
      t4 = $[1];
    }
    let t5;
    if ($[2] !== placeholder || $[3] !== users) {
      t5 = users.length === 0 ? placeholder : users.map(_temp).join(", ");
      $[2] = placeholder;
      $[3] = users;
      $[4] = t5;
    } else {
      t5 = $[4];
    }
    let t6;
    if ($[5] !== t4 || $[6] !== t5) {
      t6 = <span className={t4}>{t5}</span>;
      $[5] = t4;
      $[6] = t5;
      $[7] = t6;
    } else {
      t6 = $[7];
    }
    return t6;
  }
  if (compact && users.length === 0) {
    let t3;
    if ($[8] !== editTrigger || $[9] !== placeholder) {
      t3 = editTrigger ?? <span className="text-muted-foreground truncate">{placeholder}</span>;
      $[8] = editTrigger;
      $[9] = placeholder;
      $[10] = t3;
    } else {
      t3 = $[10];
    }
    return t3;
  }
  if (!compact && users.length === 0) {
    let t3;
    if ($[11] !== editTrigger || $[12] !== placeholder) {
      t3 = editTrigger ?? <span className="text-muted-foreground flex min-w-0 items-center gap-2"><Avatar size="sm"><AvatarFallback><CircleUserRound /></AvatarFallback></Avatar><span className="truncate">{placeholder}</span></span>;
      $[11] = editTrigger;
      $[12] = placeholder;
      $[13] = t3;
    } else {
      t3 = $[13];
    }
    return t3;
  }
  if (!compact && users.length === 1) {
    const user_0 = users[0];
    let t3;
    if ($[14] !== user_0) {
      t3 = <UserAvatar user={user_0} />;
      $[14] = user_0;
      $[15] = t3;
    } else {
      t3 = $[15];
    }
    let t4;
    if ($[16] !== user_0) {
      t4 = formatUserName(user_0);
      $[16] = user_0;
      $[17] = t4;
    } else {
      t4 = $[17];
    }
    let t5;
    if ($[18] !== t4) {
      t5 = <span className="truncate">{t4}</span>;
      $[18] = t4;
      $[19] = t5;
    } else {
      t5 = $[19];
    }
    let t6;
    if ($[20] !== t3 || $[21] !== t5) {
      t6 = <span className="flex min-w-0 items-center gap-2">{t3}{t5}</span>;
      $[20] = t3;
      $[21] = t5;
      $[22] = t6;
    } else {
      t6 = $[22];
    }
    return t6;
  }
  let T0;
  let overflowCount;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  if ($[23] !== users) {
    const visibleUsers = users.slice(0, MAX_VISIBLE_USERS);
    overflowCount = users.length - visibleUsers.length;
    T0 = AvatarGroup;
    t3 = "group/assignee-editor gap-0 transition-[gap] duration-200 ease-out hover:gap-1";
    t4 = "AvatarGroup";
    t5 = "SelectedUsers";
    t6 = "form-user-multi-select.tsx";
    t7 = visibleUsers.map(_temp2);
    $[23] = users;
    $[24] = T0;
    $[25] = overflowCount;
    $[26] = t3;
    $[27] = t4;
    $[28] = t5;
    $[29] = t6;
    $[30] = t7;
  } else {
    T0 = $[24];
    overflowCount = $[25];
    t3 = $[26];
    t4 = $[27];
    t5 = $[28];
    t6 = $[29];
    t7 = $[30];
  }
  let t8;
  if ($[31] !== overflowCount || $[32] !== users) {
    t8 = overflowCount > 0 && <Tooltip><TooltipTrigger render={<span className="inline-flex" />}><AvatarGroupCount className={AVATAR_GROUP_COUNT_BORDER_CLASS_NAME}>+{overflowCount}</AvatarGroupCount></TooltipTrigger><TooltipContent>{users.slice(MAX_VISIBLE_USERS).map(_temp3).join(", ")}</TooltipContent></Tooltip>;
    $[31] = overflowCount;
    $[32] = users;
    $[33] = t8;
  } else {
    t8 = $[33];
  }
  let t9;
  if ($[34] !== T0 || $[35] !== editTrigger || $[36] !== t3 || $[37] !== t4 || $[38] !== t5 || $[39] !== t6 || $[40] !== t7 || $[41] !== t8) {
    t9 = <T0 className={t3} data-sentry-element={t4} data-sentry-component={t5} data-sentry-source-file={t6}>{t7}{t8}{editTrigger}</T0>;
    $[34] = T0;
    $[35] = editTrigger;
    $[36] = t3;
    $[37] = t4;
    $[38] = t5;
    $[39] = t6;
    $[40] = t7;
    $[41] = t8;
    $[42] = t9;
  } else {
    t9 = $[42];
  }
  return t9;
};
const UserListSkeleton = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="flex flex-col gap-2 p-2" data-sentry-component="UserListSkeleton" data-sentry-source-file="form-user-multi-select.tsx">{Array.from({
        length: 5
      }).map(_temp4)}</div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
type GroupSelectionState = 'indeterminate' | boolean;
const getGroupSelectionState = (groupUsers: FormUserMultiSelectUser[], selectedUserIds: Set<number>): GroupSelectionState => {
  const selectedCount = groupUsers.filter(user => selectedUserIds.has(user.id)).length;
  if (selectedCount === 0) {
    return false;
  }
  if (selectedCount === groupUsers.length) {
    return true;
  }
  return 'indeterminate';
};
const GroupSelectionIcon = t0 => {
  const $ = _c(2);
  const {
    state
  } = t0;
  if (state === "indeterminate") {
    let t1;
    if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Minus className="size-3.5" />;
      $[0] = t1;
    } else {
      t1 = $[0];
    }
    return t1;
  }
  if (state) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <Check className="size-3.5" />;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  return null;
};
const UserOptions = t0 => {
  const $ = _c(40);
  const {
    currentUser,
    draftUserIds,
    groups: t1,
    isLoading,
    isLocked,
    onToggle,
    onToggleGroup,
    plainText: t2,
    users
  } = t0;
  let T0;
  let t3;
  let t4;
  let t5;
  let t6;
  let t7;
  let t8;
  let t9;
  if ($[0] !== currentUser || $[1] !== draftUserIds || $[2] !== isLoading || $[3] !== isLocked || $[4] !== onToggle || $[5] !== onToggleGroup || $[6] !== t1 || $[7] !== t2 || $[8] !== users) {
    t9 = Symbol.for("react.early_return_sentinel");
    bb0: {
      const groups = t1 === undefined ? [] : t1;
      const plainText = t2 === undefined ? false : t2;
      if (isLoading) {
        let t10;
        if ($[17] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <CommandList><div aria-label="Loading users" role="status"><UserListSkeleton /></div></CommandList>;
          $[17] = t10;
        } else {
          t10 = $[17];
        }
        t9 = t10;
        break bb0;
      }
      const groupedUserIds = new Set(groups.flatMap(_temp6));
      const availableUsers = dedupeUsers([...groups.flatMap(_temp7), ...users]);
      const pinnedCurrentUser = currentUser && availableUsers.some(user_0 => user_0.id === currentUser.id) ? currentUser : undefined;
      const ungroupedUsers = availableUsers.filter(user_1 => !groupedUserIds.has(user_1.id) && user_1.id !== pinnedCurrentUser?.id);
      if (availableUsers.length === 0) {
        let t10;
        if ($[18] === Symbol.for("react.memo_cache_sentinel")) {
          t10 = <CommandList><CommandEmpty role="status">No results found</CommandEmpty></CommandList>;
          $[18] = t10;
        } else {
          t10 = $[18];
        }
        t9 = t10;
        break bb0;
      }
      let t10;
      if ($[19] !== draftUserIds || $[20] !== isLocked || $[21] !== onToggle || $[22] !== plainText) {
        t10 = user_2 => {
          const userName = formatUserName(user_2);
          return <CommandItem aria-label={userName} data-checked={draftUserIds.has(user_2.id)} disabled={isLocked} key={user_2.id} onSelect={() => onToggle(user_2)} value={`${userName} ${user_2.email} ${user_2.id}`} data-sentry-element="CommandItem" data-sentry-component="renderUser" data-sentry-source-file="form-user-multi-select.tsx">{!plainText && <UserAvatar user={user_2} />}<span className="truncate">{userName}</span></CommandItem>;
        };
        $[19] = draftUserIds;
        $[20] = isLocked;
        $[21] = onToggle;
        $[22] = plainText;
        $[23] = t10;
      } else {
        t10 = $[23];
      }
      const renderUser = t10;
      T0 = CommandList;
      t3 = "CommandList";
      t4 = "UserOptions";
      t5 = "form-user-multi-select.tsx";
      if ($[24] !== pinnedCurrentUser || $[25] !== renderUser) {
        t6 = pinnedCurrentUser && <CommandGroup>{renderUser(pinnedCurrentUser)}</CommandGroup>;
        $[24] = pinnedCurrentUser;
        $[25] = renderUser;
        $[26] = t6;
      } else {
        t6 = $[26];
      }
      let t11;
      if ($[27] !== draftUserIds || $[28] !== isLocked || $[29] !== onToggleGroup || $[30] !== renderUser) {
        t11 = group_1 => {
          const checkedState = getGroupSelectionState(group_1.users, draftUserIds);
          return <CommandGroup key={group_1.id}><CommandItem aria-label={`Select all ${group_1.label} users`} className="bg-muted mx-1 rounded-md py-1 text-xs font-medium" data-checked={checkedState === true} disabled={isLocked || group_1.users.length === 0} onSelect={() => onToggleGroup(group_1.users)} value={`${group_1.label} ${group_1.id}`}><UsersRound className="text-muted-foreground size-3.5" /><span className="truncate">{group_1.label}</span><CommandShortcut aria-hidden="true"><GroupSelectionIcon state={checkedState} /></CommandShortcut></CommandItem>{group_1.users.map(renderUser)}</CommandGroup>;
        };
        $[27] = draftUserIds;
        $[28] = isLocked;
        $[29] = onToggleGroup;
        $[30] = renderUser;
        $[31] = t11;
      } else {
        t11 = $[31];
      }
      t7 = groups.map(t11);
      t8 = ungroupedUsers.length > 0 && <CommandGroup>{ungroupedUsers.map(renderUser)}</CommandGroup>;
    }
    $[0] = currentUser;
    $[1] = draftUserIds;
    $[2] = isLoading;
    $[3] = isLocked;
    $[4] = onToggle;
    $[5] = onToggleGroup;
    $[6] = t1;
    $[7] = t2;
    $[8] = users;
    $[9] = T0;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
    $[14] = t7;
    $[15] = t8;
    $[16] = t9;
  } else {
    T0 = $[9];
    t3 = $[10];
    t4 = $[11];
    t5 = $[12];
    t6 = $[13];
    t7 = $[14];
    t8 = $[15];
    t9 = $[16];
  }
  if (t9 !== Symbol.for("react.early_return_sentinel")) {
    return t9;
  }
  let t10;
  if ($[32] !== T0 || $[33] !== t3 || $[34] !== t4 || $[35] !== t5 || $[36] !== t6 || $[37] !== t7 || $[38] !== t8) {
    t10 = <T0 data-sentry-element={t3} data-sentry-component={t4} data-sentry-source-file={t5}>{t6}{t7}{t8}</T0>;
    $[32] = T0;
    $[33] = t3;
    $[34] = t4;
    $[35] = t5;
    $[36] = t6;
    $[37] = t7;
    $[38] = t8;
    $[39] = t10;
  } else {
    t10 = $[39];
  }
  return t10;
};
export const UserMultiSelect = t0 => {
  const $ = _c(99);
  const {
    className,
    compact: t1,
    currentUser,
    disabled: t2,
    groups,
    id,
    invalid: t3,
    isLoading: t4,
    isPending: t5,
    label,
    onBlur,
    onChange,
    onOpenChange,
    onSearchValueChange,
    placeholder: t6,
    plainText: t7,
    readOnly,
    searchPlaceholder: t8,
    searchValue,
    users,
    value
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const invalid = t3 === undefined ? false : t3;
  const isLoading = t4 === undefined ? false : t4;
  const isPending = t5 === undefined ? false : t5;
  const placeholder = t6 === undefined ? "-" : t6;
  const plainText = t7 === undefined ? false : t7;
  const searchPlaceholder = t8 === undefined ? "Search users" : t8;
  const isReadOnly = useReadOnly(readOnly);
  let t9;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = [];
    $[0] = t9;
  } else {
    t9 = $[0];
  }
  const [draftUsers, setDraftUsers] = useState(t9);
  const [open, setOpen] = useState(false);
  const closeHandled = useRef(false);
  const lockClosePending = useRef(false);
  let t10;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = [];
    $[1] = t10;
  } else {
    t10 = $[1];
  }
  const openingUsers = useRef(t10);
  let t11;
  if ($[2] !== value) {
    t11 = dedupeUsers(value);
    $[2] = value;
    $[3] = t11;
  } else {
    t11 = $[3];
  }
  const selectedUsers = t11;
  let t12;
  if ($[4] !== groups || $[5] !== users) {
    t12 = dedupeUsers([...(groups?.flatMap(_temp8) ?? []), ...users]);
    $[4] = groups;
    $[5] = users;
    $[6] = t12;
  } else {
    t12 = $[6];
  }
  const availableUsers = t12;
  let t13;
  if ($[7] !== draftUsers) {
    t13 = new Set(draftUsers.map(_temp9));
    $[7] = draftUsers;
    $[8] = t13;
  } else {
    t13 = $[8];
  }
  const draftUserIds = t13;
  const isLocked = disabled || isPending || isReadOnly;
  let t14;
  if ($[9] !== onOpenChange) {
    t14 = nextOpen => {
      setOpen(nextOpen);
      onOpenChange?.(nextOpen);
    };
    $[9] = onOpenChange;
    $[10] = t14;
  } else {
    t14 = $[10];
  }
  const setEditorOpen = t14;
  let t15;
  let t16;
  if ($[11] !== isLocked || $[12] !== onBlur || $[13] !== onOpenChange || $[14] !== open) {
    t15 = () => {
      if (!isLocked || !open) {
        return;
      }
      closeHandled.current = true;
      lockClosePending.current = true;
      setDraftUsers(openingUsers.current);
      onBlur?.();
      setOpen(false);
      onOpenChange?.(false);
    };
    t16 = [isLocked, onBlur, onOpenChange, open];
    $[11] = isLocked;
    $[12] = onBlur;
    $[13] = onOpenChange;
    $[14] = open;
    $[15] = t15;
    $[16] = t16;
  } else {
    t15 = $[15];
    t16 = $[16];
  }
  useEffect(t15, t16);
  let t17;
  if ($[17] !== onSearchValueChange) {
    t17 = nextOpen_0 => {
      if (nextOpen_0 || !lockClosePending.current) {
        return;
      }
      lockClosePending.current = false;
      onSearchValueChange("");
    };
    $[17] = onSearchValueChange;
    $[18] = t17;
  } else {
    t17 = $[18];
  }
  const handleOpenChangeComplete = t17;
  let t18;
  if ($[19] !== draftUsers || $[20] !== isLocked || $[21] !== onBlur || $[22] !== onChange || $[23] !== onSearchValueChange || $[24] !== selectedUsers || $[25] !== setEditorOpen) {
    t18 = (nextOpen_1, eventDetails) => {
      if (nextOpen_1) {
        if (isLocked) {
          return;
        }
        openingUsers.current = selectedUsers;
        closeHandled.current = false;
        setDraftUsers(selectedUsers);
        setEditorOpen(true);
        return;
      }
      if (closeHandled.current) {
        return;
      }
      closeHandled.current = true;
      if (isLocked) {
        setDraftUsers(openingUsers.current);
      } else {
        if (eventDetails.reason !== "escape-key" && !haveSameUserIds(selectedUsers, draftUsers)) {
          onChange(draftUsers);
        } else {
          if (eventDetails.reason === "escape-key") {
            setDraftUsers(openingUsers.current);
          }
        }
      }
      onBlur?.();
      onSearchValueChange("");
      setEditorOpen(false);
    };
    $[19] = draftUsers;
    $[20] = isLocked;
    $[21] = onBlur;
    $[22] = onChange;
    $[23] = onSearchValueChange;
    $[24] = selectedUsers;
    $[25] = setEditorOpen;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  const handleOpenChange = t18;
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = user_0 => {
      setDraftUsers(currentUsers => {
        if (currentUsers.some(currentUser_1 => currentUser_1.id === user_0.id)) {
          return currentUsers.filter(currentUser_0 => currentUser_0.id !== user_0.id);
        }
        return [...currentUsers, user_0];
      });
    };
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  const handleToggleUser = t19;
  let t20;
  if ($[28] === Symbol.for("react.memo_cache_sentinel")) {
    t20 = groupUsers => {
      setDraftUsers(currentUsers_0 => {
        const currentUserIds = new Set(currentUsers_0.map(_temp10));
        const visibleUserIds = new Set(groupUsers.map(_temp11));
        const allUsersSelected = groupUsers.every(user_3 => currentUserIds.has(user_3.id));
        if (allUsersSelected) {
          return currentUsers_0.filter(user_4 => !visibleUserIds.has(user_4.id));
        }
        return [...currentUsers_0, ...groupUsers.filter(user_5 => !currentUserIds.has(user_5.id))];
      });
    };
    $[28] = t20;
  } else {
    t20 = $[28];
  }
  const handleToggleGroup = t20;
  let t21;
  if ($[29] !== handleOpenChange) {
    t21 = event => {
      if (event.relatedTarget instanceof Node && event.currentTarget.contains(event.relatedTarget)) {
        return;
      }
      handleOpenChange(false, {
        reason: "focus-out"
      });
    };
    $[29] = handleOpenChange;
    $[30] = t21;
  } else {
    t21 = $[30];
  }
  const handleContentBlur = t21;
  if (isReadOnly) {
    const t22 = label ?? "Selected users";
    let t23;
    if ($[31] !== className) {
      t23 = cn("flex max-w-full items-center gap-2", className);
      $[31] = className;
      $[32] = t23;
    } else {
      t23 = $[32];
    }
    let t24;
    if ($[33] !== compact || $[34] !== placeholder || $[35] !== plainText || $[36] !== selectedUsers) {
      t24 = <SelectedUsers compact={compact} placeholder={placeholder} plainText={plainText} users={selectedUsers} />;
      $[33] = compact;
      $[34] = placeholder;
      $[35] = plainText;
      $[36] = selectedUsers;
      $[37] = t24;
    } else {
      t24 = $[37];
    }
    let t25;
    if ($[38] !== id || $[39] !== t22 || $[40] !== t23 || $[41] !== t24) {
      t25 = <div aria-label={t22} className={t23} id={id} role="group">{t24}</div>;
      $[38] = id;
      $[39] = t22;
      $[40] = t23;
      $[41] = t24;
      $[42] = t25;
    } else {
      t25 = $[42];
    }
    return t25;
  }
  const t22 = `Edit ${label ?? "users"}`;
  const t23 = selectedUsers.length > 0 && EDIT_TRIGGER_HOVER_CLASS_NAME;
  let t24;
  if ($[43] !== t23) {
    t24 = cn(TRIGGER_CLASS_NAME, t23);
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <Avatar size="sm"><AvatarFallback><Pencil /></AvatarFallback></Avatar>;
    $[45] = t25;
  } else {
    t25 = $[45];
  }
  let t26;
  if ($[46] !== id || $[47] !== invalid || $[48] !== isLocked || $[49] !== t22 || $[50] !== t24) {
    t26 = <PopoverTrigger asChild={true}><Button aria-invalid={invalid} aria-label={t22} className={t24} disabled={isLocked} id={id} onClick={_temp12} onKeyDown={_temp13} onPointerDown={_temp14} size="icon-xs" type="button" variant="no-style">{t25}</Button></PopoverTrigger>;
    $[46] = id;
    $[47] = invalid;
    $[48] = isLocked;
    $[49] = t22;
    $[50] = t24;
    $[51] = t26;
  } else {
    t26 = $[51];
  }
  const editTrigger = t26;
  let t27;
  if ($[52] !== className) {
    t27 = cn("flex max-w-full items-center gap-2", className);
    $[52] = className;
    $[53] = t27;
  } else {
    t27 = $[53];
  }
  const t28 = open && !isLocked;
  let t29;
  if ($[54] !== compact || $[55] !== editTrigger || $[56] !== id || $[57] !== invalid || $[58] !== isLocked || $[59] !== isPending || $[60] !== label || $[61] !== placeholder || $[62] !== plainText || $[63] !== selectedUsers) {
    t29 = !plainText && (compact || selectedUsers.length === 0 || selectedUsers.length > 1) ? <SelectedUsers compact={compact} editTrigger={editTrigger} placeholder={placeholder} users={selectedUsers} /> : <PopoverTrigger asChild={true}><Button align="left" aria-invalid={invalid} aria-label={`Edit ${label ?? "users"}`} className="w-full" disabled={isLocked} id={id} size={plainText ? 1 : "default"} type="button" variant="ghost"><SelectedUsers placeholder={placeholder} plainText={plainText} users={selectedUsers} />{isPending && <Spinner data-icon="inline-end" />}</Button></PopoverTrigger>;
    $[54] = compact;
    $[55] = editTrigger;
    $[56] = id;
    $[57] = invalid;
    $[58] = isLocked;
    $[59] = isPending;
    $[60] = label;
    $[61] = placeholder;
    $[62] = plainText;
    $[63] = selectedUsers;
    $[64] = t29;
  } else {
    t29 = $[64];
  }
  let t30;
  if ($[65] !== isLocked || $[66] !== onSearchValueChange || $[67] !== searchPlaceholder || $[68] !== searchValue) {
    t30 = <CommandInput aria-label={searchPlaceholder} disabled={isLocked} onValueChange={onSearchValueChange} placeholder={searchPlaceholder} value={searchValue} data-sentry-element="CommandInput" data-sentry-source-file="form-user-multi-select.tsx" />;
    $[65] = isLocked;
    $[66] = onSearchValueChange;
    $[67] = searchPlaceholder;
    $[68] = searchValue;
    $[69] = t30;
  } else {
    t30 = $[69];
  }
  let t31;
  if ($[70] !== availableUsers || $[71] !== currentUser || $[72] !== draftUserIds || $[73] !== groups || $[74] !== isLoading || $[75] !== isLocked || $[76] !== plainText) {
    t31 = <UserOptions currentUser={currentUser} draftUserIds={draftUserIds} groups={groups} isLoading={isLoading} isLocked={isLocked} onToggle={handleToggleUser} onToggleGroup={handleToggleGroup} plainText={plainText} users={availableUsers} data-sentry-element="UserOptions" data-sentry-source-file="form-user-multi-select.tsx" />;
    $[70] = availableUsers;
    $[71] = currentUser;
    $[72] = draftUserIds;
    $[73] = groups;
    $[74] = isLoading;
    $[75] = isLocked;
    $[76] = plainText;
    $[77] = t31;
  } else {
    t31 = $[77];
  }
  let t32;
  if ($[78] !== t30 || $[79] !== t31) {
    t32 = <Command shouldFilter={false} data-sentry-element="Command" data-sentry-source-file="form-user-multi-select.tsx">{t30}{t31}</Command>;
    $[78] = t30;
    $[79] = t31;
    $[80] = t32;
  } else {
    t32 = $[80];
  }
  let t33;
  if ($[81] !== handleContentBlur || $[82] !== t32) {
    t33 = <PopoverContent align="start" className="p-0" onBlur={handleContentBlur} data-sentry-element="PopoverContent" data-sentry-source-file="form-user-multi-select.tsx">{t32}</PopoverContent>;
    $[81] = handleContentBlur;
    $[82] = t32;
    $[83] = t33;
  } else {
    t33 = $[83];
  }
  let t34;
  if ($[84] !== handleOpenChange || $[85] !== handleOpenChangeComplete || $[86] !== t28 || $[87] !== t29 || $[88] !== t33) {
    t34 = <Popover onOpenChange={handleOpenChange} onOpenChangeComplete={handleOpenChangeComplete} open={t28} data-sentry-element="Popover" data-sentry-source-file="form-user-multi-select.tsx">{t29}{t33}</Popover>;
    $[84] = handleOpenChange;
    $[85] = handleOpenChangeComplete;
    $[86] = t28;
    $[87] = t29;
    $[88] = t33;
    $[89] = t34;
  } else {
    t34 = $[89];
  }
  let t35;
  if ($[90] !== compact || $[91] !== isPending || $[92] !== plainText || $[93] !== selectedUsers.length) {
    t35 = isPending && !plainText && (compact || selectedUsers.length > 1) && <Spinner />;
    $[90] = compact;
    $[91] = isPending;
    $[92] = plainText;
    $[93] = selectedUsers.length;
    $[94] = t35;
  } else {
    t35 = $[94];
  }
  let t36;
  if ($[95] !== t27 || $[96] !== t34 || $[97] !== t35) {
    t36 = <div className={t27} data-sentry-component="UserMultiSelect" data-sentry-source-file="form-user-multi-select.tsx">{t34}{t35}</div>;
    $[95] = t27;
    $[96] = t34;
    $[97] = t35;
    $[98] = t36;
  } else {
    t36 = $[98];
  }
  return t36;
};
export const FormUserMultiSelect = t0 => {
  const $ = _c(35);
  const {
    className,
    compact: t1,
    currentUser,
    disabled: t2,
    groups,
    isLoading: t3,
    isPending: t4,
    label,
    onOpenChange,
    onSearchValueChange,
    placeholder: t5,
    plainText: t6,
    readOnly,
    searchPlaceholder: t7,
    searchValue,
    users
  } = t0;
  const compact = t1 === undefined ? false : t1;
  const disabled = t2 === undefined ? false : t2;
  const isLoading = t3 === undefined ? false : t3;
  const isPending = t4 === undefined ? false : t4;
  const placeholder = t5 === undefined ? "-" : t5;
  const plainText = t6 === undefined ? false : t6;
  const searchPlaceholder = t7 === undefined ? "Search users" : t7;
  const field = useFieldContext();
  const triggerId = useId();
  const isInvalid = field.state.meta.errors.length > 0;
  const t8 = disabled || isPending;
  let t9;
  if ($[0] !== label || $[1] !== triggerId) {
    t9 = label && <FieldLabel htmlFor={triggerId}>{label}</FieldLabel>;
    $[0] = label;
    $[1] = triggerId;
    $[2] = t9;
  } else {
    t9 = $[2];
  }
  let t10;
  if ($[3] !== field.state.value) {
    t10 = field.state.value ?? [];
    $[3] = field.state.value;
    $[4] = t10;
  } else {
    t10 = $[4];
  }
  let t11;
  if ($[5] !== className || $[6] !== compact || $[7] !== currentUser || $[8] !== disabled || $[9] !== field.handleBlur || $[10] !== field.handleChange || $[11] !== groups || $[12] !== isInvalid || $[13] !== isLoading || $[14] !== isPending || $[15] !== label || $[16] !== onOpenChange || $[17] !== onSearchValueChange || $[18] !== placeholder || $[19] !== plainText || $[20] !== readOnly || $[21] !== searchPlaceholder || $[22] !== searchValue || $[23] !== t10 || $[24] !== triggerId || $[25] !== users) {
    t11 = <FieldContent data-sentry-element="FieldContent" data-sentry-source-file="form-user-multi-select.tsx"><UserMultiSelect className={className} compact={compact} currentUser={currentUser} disabled={disabled} groups={groups} id={triggerId} invalid={isInvalid} isLoading={isLoading} isPending={isPending} label={label} onBlur={field.handleBlur} onChange={field.handleChange} onOpenChange={onOpenChange} onSearchValueChange={onSearchValueChange} placeholder={placeholder} plainText={plainText} readOnly={readOnly} searchPlaceholder={searchPlaceholder} searchValue={searchValue} users={users} value={t10} data-sentry-element="UserMultiSelect" data-sentry-source-file="form-user-multi-select.tsx" /></FieldContent>;
    $[5] = className;
    $[6] = compact;
    $[7] = currentUser;
    $[8] = disabled;
    $[9] = field.handleBlur;
    $[10] = field.handleChange;
    $[11] = groups;
    $[12] = isInvalid;
    $[13] = isLoading;
    $[14] = isPending;
    $[15] = label;
    $[16] = onOpenChange;
    $[17] = onSearchValueChange;
    $[18] = placeholder;
    $[19] = plainText;
    $[20] = readOnly;
    $[21] = searchPlaceholder;
    $[22] = searchValue;
    $[23] = t10;
    $[24] = triggerId;
    $[25] = users;
    $[26] = t11;
  } else {
    t11 = $[26];
  }
  let t12;
  if ($[27] !== field.state.meta.errors) {
    t12 = <FieldError errors={field.state.meta.errors} data-sentry-element="FieldError" data-sentry-source-file="form-user-multi-select.tsx" />;
    $[27] = field.state.meta.errors;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  let t13;
  if ($[29] !== isInvalid || $[30] !== t11 || $[31] !== t12 || $[32] !== t8 || $[33] !== t9) {
    t13 = <Field data-disabled={t8} data-invalid={isInvalid} data-sentry-element="Field" data-sentry-component="FormUserMultiSelect" data-sentry-source-file="form-user-multi-select.tsx">{t9}{t11}{t12}</Field>;
    $[29] = isInvalid;
    $[30] = t11;
    $[31] = t12;
    $[32] = t8;
    $[33] = t9;
    $[34] = t13;
  } else {
    t13 = $[34];
  }
  return t13;
};
function _temp(user) {
  return formatUserName(user);
}
function _temp2(user_1) {
  return <UserAvatarWithTooltip key={user_1.id} user={user_1} />;
}
function _temp3(user_2) {
  return formatUserName(user_2);
}
function _temp4(_, index) {
  return <Skeleton className="h-12 w-full" key={index} />;
}
function _temp5(user) {
  return user.id;
}
function _temp6(group) {
  return group.users.map(_temp5);
}
function _temp7(group_0) {
  return group_0.users;
}
function _temp8(group) {
  return group.users;
}
function _temp9(user) {
  return user.id;
}
function _temp10(user_1) {
  return user_1.id;
}
function _temp11(user_2) {
  return user_2.id;
}
function _temp12(event_0) {
  event_0.preventDefault();
  event_0.stopPropagation();
}
function _temp13(event_1) {
  return event_1.stopPropagation();
}
function _temp14(event_2) {
  return event_2.stopPropagation();
}